import React from 'react'
import cn from 'classnames'
import cls from './styles.module.scss'

type HighlightRule = {
    word: string
    className?: string
    style?: React.CSSProperties
}

interface HighlightedTextProps {
    text: string
    highlights?: HighlightRule[]
    breakAfterWords?: string[]
    breakBeforeWords?: string[]
    className?: string
}

export const HighlightedText: React.FC<HighlightedTextProps> = ({
    text,
    highlights = [],
    breakAfterWords = [],
    breakBeforeWords = [],
    className,
}) => {
    const highlightMap = new Map<string, HighlightRule>()
    highlights.forEach(({ word, ...rest }) => {
        highlightMap.set(word.toLowerCase(), { word, ...rest })
    })

    const allWords = [
        ...highlights.map((h) => h.word),
        ...breakAfterWords,
        ...breakBeforeWords,
    ]
    const escaped = allWords.map((word) =>
        word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    )
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi')
    const parts = text.split(regex)

    return (
        <span className={cn(cls.main_text, className)}>
            {parts.map((part, index) => {
                const key = part.toLowerCase()
                const rule = highlightMap.get(key)
                const shouldBreakAfter = breakAfterWords.some(
                    (w) => w.toLowerCase() === key
                )
                const shouldBreakBefore = breakBeforeWords.some(
                    (w) => w.toLowerCase() === key
                )

                const content = rule ? (
                    <span
                        className={cn(cls.main_text_yellow, rule.className)}
                        style={rule.style}
                    >
                        {part}
                    </span>
                ) : (
                    part
                )

                const elements: React.ReactNode[] = []

                if (shouldBreakBefore) {
                    elements.push(<br key={`br-before-${index}`} />)
                }

                elements.push(
                    <React.Fragment key={index}>{content}</React.Fragment>
                )

                if (shouldBreakAfter) {
                    elements.push(<br key={`br-after-${index}`} />)
                }

                return elements
            })}
        </span>
    )
}
